import { useEffect } from 'react';

// Import all onboarding images
// Step 1: Knowledge Base
import knowledgeBaseIllustration from '@/assets/onboarding/step-1-knowledge-base.jpg';

// Step 2: Shooting Setup
import shootingSetupIllustration from '@/assets/onboarding/step-2-shooting-setup.jpg';
import installationFauteuil from '@/assets/installation-fauteuil.png';
import installationTabouret from '@/assets/installation-tabouret.png';
import installationBureau from '@/assets/installation-bureau.png';

// Step 3: Decor Selection
import decorPreview from '@/assets/decor-preview.png';

// Step 4: Thumbnail Selection
import thumbnailStyleIllustration from '@/assets/onboarding/step-4-thumbnail-style.jpg';
import boldThumbnail from '@/assets/thumbnails/da-bold.png';
import classiqueThumbnail from '@/assets/thumbnails/da-classique.png';
import customThumbnail from '@/assets/thumbnails/da-custom.png';
import boldOverlay from '@/assets/thumbnails/bold-overlay.png';
import classiqueOverlay from '@/assets/thumbnails/classique-overlay.png';

// Step 5: Network Connection (matches OnboardingFlow.tsx)
import networkConnectionIllustration from '@/assets/onboarding/step-5-network-connection.png';

// Step 6: Editorial Planning
import editorialPlanningIllustration from '@/assets/onboarding/step-6-editorial-planning.jpg';

// Step 7: Cross-post
import crossPostIllustration from '@/assets/crosspost.png';

// All images to preload in parallel
const ONBOARDING_IMAGES = [
  // Step 1
  knowledgeBaseIllustration,
  // Step 2
  shootingSetupIllustration,
  installationFauteuil,
  installationTabouret,
  installationBureau,
  // Step 3
  decorPreview,
  // Step 4
  thumbnailStyleIllustration,
  boldThumbnail,
  classiqueThumbnail,
  customThumbnail,
  boldOverlay,
  classiqueOverlay,
  // Step 5
  networkConnectionIllustration,
  // Step 6
  editorialPlanningIllustration,
  // Step 7
  crossPostIllustration,
];

/**
 * Handles image load event by resolving the promise
 */
function handleImageLoad(resolve: () => void): void {
  resolve();
}

/**
 * Handles image error event by logging a warning and resolving to not block other images
 */
function handleImageError(src: string, resolve: () => void): void {
  console.warn(`[Onboarding] Failed to preload: ${src}`);
  resolve();
}

/**
 * Preloads a single image and returns a promise that resolves when loaded or on error
 */
function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => handleImageLoad(resolve);
    img.onerror = () => handleImageError(src, resolve);
    img.src = src;
  });
}

/**
 * Preloads all onboarding images in parallel
 */
async function preloadAllImages(): Promise<void> {
  await Promise.all(ONBOARDING_IMAGES.map(preloadImage));
}

/**
 * Preloads all onboarding images in parallel on mount.
 * This ensures images are cached and ready before the user navigates to steps that use them.
 */
export function usePreloadOnboardingImages() {
  useEffect(() => {
    let isCancelled = false;

    preloadAllImages().then(() => {
      if (!isCancelled) {
        console.log(`[Onboarding] All ${ONBOARDING_IMAGES.length} images preloaded`);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, []);
}
